import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandProfileAnalysis } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_profile_analysis: BrandProfileAnalysis
}

export interface Variables {
  prompt?: string
  signal?: AbortSignal
  skipProfileUpdate?: boolean
}

export interface Props {
  brandProfileId: number
}

export const createBrandProfileAnalysis = async ({
  brandProfileId,
  workspaceId,
  prompt,
  signal,
  skipProfileUpdate,
}: Variables & Props & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_profiles/${brandProfileId}/analyses`, {
    method: 'POST',
    body: {
      prompt,
      skip_profile_update: skipProfileUpdate,
    },
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandProfileAnalysis, Error, Variables>, 'mutationFn'> {
  onCreate?: (analysis: BrandProfileAnalysis) => void
}

export const useCreateBrandProfileAnalysis = ({ brandProfileId }: Props, options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()

  return useMutation<BrandProfileAnalysis, Error, Variables>(async (vars) => {
    const { prompt, signal, skipProfileUpdate } = vars

    const result = await createBrandProfileAnalysis({
      workspaceId,
      prompt,
      signal,
      skipProfileUpdate,
      brandProfileId,
    })

    return result.brand_profile_analysis
  }, options)
}
